import { Box, Stack, Typography, Link } from "@mui/material";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import TkDownloadLink from "../../components/atoms/TkDownloadLink";
import { GlobalContext } from "../../context/globalContext";
import { BusinessContext } from "../../context/businessContext";
import "./_style.scss";
import { httpAPI_SUB001 } from "./api/request";
import ScrollTable from "./components";
import { TkGetCode } from "../../utils/TkGetCode";
import {
  FormClassApply,
  Prefectures,
  SubsidyApplyKbn,
} from "../../assets/data/Constants";
import { changeAmountFormat } from "../../utils/useTools";
import { FormList } from "../../type/apiType/IAPI_SUB001";
import { formAreaList, searchFormData } from "./parts";
import TkSearchForm from "../../components/molecules/TkSearchForm";
import _ from "lodash";

/**
 * SCRW210001 補助金交付申請状況一覧
 * 機構職員が国・各都道府県の補助金交付申請状況の一覧を確認する。
 * @return SCRW210001 補助金交付申請状況一覧
 */
const SCRW210001 = () => {
  const navigate = useNavigate();
  const { state, setState } = useContext(GlobalContext);
  const { state: businessState, setState: setBusinessState } =
    useContext(BusinessContext);
  const context = businessState.job_82;
  const [stateSearchList, setStateSearchList] = useState<any>();
  const searchList = useRef(_.cloneDeep(searchFormData));
  const [total, setTotal] = useState<any>([]);
  const [bodyList, setBodyList] = useState<any>([]);
  const [maxTimes, setMaxTimes] = useState(0);
  const [formList, setFormList] = useState<FormList[]>([]);

  const handleLinkClick = (state: any, search?: any) => {
    setBusinessState({
      ...businessState,
      job_82: {
        ...search,
      },
    });
    navigate("/SCRW210002", { state });
  };

  // 補助金交付申請一覧参照API呼び出し
  const handleCallApiInit = async (param: any) => {
    try {
      const res = await httpAPI_SUB001({
        year: param.year,
        prefectureCd: param.prefectureCd,
      });

      if (res.resultCd === "I0000") {
        const newList = res.subsidyIssueApplyList?.map((item: any) => {
          if (item?.paymentTimesList.length > res?.maxPaymentCnt!) {
            return {
              ...item,
              paymentTimesList: item?.paymentTimesList.slice(
                0,
                res?.maxPaymentCnt!
              ),
            };
          } else {
            const makeList = item?.paymentTimesList;
            for (
              let i = item?.paymentTimesList.length;
              i < res?.maxPaymentCnt!;
              i++
            ) {
              makeList.push({});
            }
            return { ...item, paymentTimesList: makeList };
          }
        });

        let bodyList = newList?.map((item: any) =>
          [
            {
              children: TkGetCode(Prefectures, item.prefectureCd),
              sticky: true,
            },
            {
              children: (
                <Link
                  onClick={() =>
                    handleLinkClick(
                      {
                        applyId: item.applyId,
                        year: res.year,
                        prefectureCd: item.prefectureCd,
                        subsidyApplyKbn: SubsidyApplyKbn["概算交付申請"],
                      },
                      param
                    )
                  }
                >
                  {changeAmountFormat(item.estimatePayApplyAmt)}
                </Link>
              ),
              align: "right",
              sticky: true,
            },
            {
              children: (
                <Link
                  onClick={() =>
                    handleLinkClick(
                      {
                        applyId: item.applyId,
                        year: res.year,
                        prefectureCd: item.prefectureCd,
                        subsidyApplyKbn: SubsidyApplyKbn["追加交付申請"],
                      },
                      param
                    )
                  }
                >
                  {changeAmountFormat(item.addApplyPayAmt)}
                </Link>
              ),
              align: "right",
              sticky: true,
            },
            {
              children: changeAmountFormat(item.subsidyTotal),
              align: "right",
              sticky: true,
            },
            {
              children: (
                <>
                  <Stack
                    direction={"row"}
                    alignItems="center"
                    justifyContent="right"
                    fontSize={"0.875rem"}
                  >
                    {item.subsidyTotal > item.amtTotal && (
                      <PriorityHighIcon className="priorityHighIcon" />
                    )}
                    {changeAmountFormat(item.amtTotal)}
                  </Stack>
                </>
              ),
              align: "right",
              sticky: true,
            },
          ].concat(
            ...item.paymentTimesList?.map((item: any) => {
              return [
                { children: item.paymentConfirmDate, align: "center" },
                { children: item.billingDate, align: "center" },
                { children: item.depositDueDate, align: "center" },
                { children: item.depositDate, align: "center" },
                { children: changeAmountFormat(item.amt), align: "right" },
              ];
            })
          )
        );

        setBodyList(bodyList);

        setMaxTimes(res?.maxPaymentCnt ?? 1);

        setTotal([
          res.estimatePayApplyAmtTotal || 0,
          res.addApplyPayAmtTotal || 0,
          res.subsidyPrefectureTotal || 0,
          res.amtPrefectureTotal || 0,
        ]);

        setFormList(res.formList ?? []);

        setState({
          ...state,
          errorMsgId: "",
        });
      } else {
        setState({
          ...state,
          errorMsgId: res.resultCd,
        });
      }
    } catch (error) {
      navigate("/error");
    }
  };

  useEffect(() => {
    let resArr = searchList.current;
    let searchYear = resArr.find((item: any) => item.keyword === "year")!;
    let prefecture = resArr.find(
      (item: any) => item.keyword === "prefectureCd"
    )!;
    const date = new Date();
    const month = date.getMonth() + 1;
    const year = date.getFullYear();
    const thisYear = month > 3 ? year : year - 1;
    let defYear = context ? context?.year : thisYear;
    let array = [];
    for (let i = 2024; i <= thisYear; i++) {
      array.push({ value: i, label: `${i}年度` });
    }
    searchYear.selectPropsList = array;
    searchYear.wantToSetValue = defYear;
    prefecture.wantToSetValue = context?.prefectureCd;
    setStateSearchList([...resArr]);

    handleCallApiInit({
      year: defYear,
      prefectureCd: context?.prefectureCd,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box className="SCRW210001" data-testid="SCRW210001PageForTest">
      <TkSearchForm
        listData={stateSearchList}
        buttonText="表示"
        onSearch={handleCallApiInit}
      />

      <ScrollTable bodyList={bodyList} totalList={total} maxTimes={maxTimes} />

      {formAreaList.map((item, index) => (
        <Box className="formArea" key={item.subsidyApplyKbn}>
          <Typography>{item.header}</Typography>
          {item.formClass.map((file) => {
            let target = formList.find(
              (i) =>
                i.subsidyApplyKbn === item.subsidyApplyKbn &&
                i.formClass === file
            );

            if (target?.formOutputStatus === "3")
              return (
                <Box>
                  <TkDownloadLink
                    key={`${index}-${file}`}
                    dataname={TkGetCode(FormClassApply, file)}
                    base64filedata={target.fileBase64!}
                    downloadBase64
                  />
                </Box>
              );

            return null;
          })}
        </Box>
      ))}
    </Box>
  );
};

export default SCRW210001;
